import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, UntypedFormGroup} from '@angular/forms';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {ReportTemplate, ReportTemplateType} from '../../../../../report/types';
import {ReportService} from '@webmate-web-core/shared/services/report.service';
import {faPlus, faSave} from '@fortawesome/pro-solid-svg-icons';
import {SectionGeneratorConfig, StaticSectionGeneratorConfig} from '@webmate-web-core/shared/types/audit';
import {getRandomUUID} from '@webmate-web-core/shared/utils/utils';
import {makeFormFromSectionGeneratorConfig} from '@webmate-web-core/projects/project/auditlab/audits/report-section-generators.component';

function makeMinimalTemplate(templateType: ReportTemplateType): ReportTemplate {
    switch (templateType) {
        case 'audit':
            return {
                id: 'blank',
                name: 'Blank Template',
                description: 'Start from scratch',
                language: 'en',
                templateType: 'audit',
                structure: {
                    reportName: 'New Report',
                    description: '',
                    parameters: {},
                    sectionGenerators: [{
                        type: 'StaticSectionGeneratorConfig',
                        id: getRandomUUID(),
                        title: 'Introduction',
                        content: [],
                        subsections: []
                    } as StaticSectionGeneratorConfig]
                },
                isBaseTemplate: false
            };
        case 'test_run':
            console.error('Test Run templates are not supported yet');
            return null as any;
        case 'finding':
            console.error('Finding templates are not supported yet');
            return null as any;
    }
}


export class CreateReportTemplateToolConfig implements WebmateToolConfig {
    scrollable = true;

    constructor(
        public toolName: string = 'Create Report Template',
        public projectId: ProjectId,
        public templateType: ReportTemplateType,
        public baseTemplate?: ReportTemplate,
        public onTemplateCreated?: (template: ReportTemplate) => void
    ) {}
}

@Component({
    selector: 'wm-create-report-template-tool',
    templateUrl: './create-report-template-tool.component.html',
    styleUrls: ['./create-report-template-tool.component.less']
})
export class CreateReportTemplateToolComponent implements OnInit {

    readonly faPlus = faPlus;
    readonly faSave = faSave;

    step: 'choose-base' | 'edit' = 'choose-base';

    availableTemplates: ReportTemplate[] = [];
    loadingTemplates = true;

    selectedBase?: ReportTemplate;

    form: UntypedFormGroup;
    sections: SectionGeneratorConfig[] = [];

    saving = false;
    errorMessage?: string;

    constructor(
        @Inject(TOOL_CONTAINER_DATA) public config: CreateReportTemplateToolConfig,
        private fb: FormBuilder,
        private reportService: ReportService,
        private toolService: ModalToolContainerService
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            name: [''],
            description: [''],
            language: ['en'],
            subsections: this.fb.group({})
        });

        this.reportService.getReportTemplatesForProject$(this.config.projectId).subscribe(templates => {
            this.availableTemplates = templates;
            this.loadingTemplates = false;
        });

        if (this.config.baseTemplate) {
            this.selectBase(this.config.baseTemplate);
        }
    }

    get blankTemplate(): ReportTemplate {
        return makeMinimalTemplate(this.config.templateType);
    }

    get subsectionsFormGroup(): UntypedFormGroup {
        return this.form.get('subsections') as UntypedFormGroup;
    }

    selectBase(template: ReportTemplate) {
        this.selectedBase = template;
        if (!this.selectedBase) return;

        this.sections = [...(template.structure?.sectionGenerators || [])];

        const subsectionControls: any = {};
        for (const section of this.sections) {
            subsectionControls[section.id] = makeFormFromSectionGeneratorConfig(this.fb, section);
        }
        this.form.setControl('subsections', this.fb.group(subsectionControls));

        const baseName = template.id === 'blank' ? 'New Template' : `${template.name} (copy)`;
        this.form.patchValue({
            name: baseName,
            description: template.description || '',
            language: template.language || 'en'
        });

        this.step = 'edit';
    }

    onGeneratorsChanged(sections: SectionGeneratorConfig[]) {
        this.sections = sections;
    }

    save() {
        if (!this.selectedBase || !this.form.valid) return;

        const formValue = this.form.value;
        const subsectionsValue = formValue.subsections || {};

        const sectionGenerators = this.sections.map(section => {
            const sv = subsectionsValue[section.id];
            return sv ? { ...section, title: sv.title } : section;
        });

        const structure = {
            ...this.selectedBase.structure,
            sectionGenerators
        };

        const payload: Omit<ReportTemplate, 'id' | 'isBaseTemplate'> = {
            name: formValue.name,
            description: formValue.description,
            language: formValue.language,
            templateType: 'audit',
            structure
        };

        this.saving = true;
        this.errorMessage = undefined;

        this.reportService.createReportTemplate$(this.config.projectId, payload).subscribe({
            next: (created) => {
                this.saving = false;
                if (this.config.onTemplateCreated) {
                    this.config.onTemplateCreated(created);
                }
                this.toolService.closeLastTool();
            },
            error: (e) => {
                this.saving = false;
                this.errorMessage = e?.error || 'Failed to create template';
            }
        });
    }
}
